import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppLayout from "../layouts/AppLayout.jsx";
import NavSocialMedia from "../components/NavSocialMedia.jsx";
import ReelCard from "../components/ReelCard.jsx";
import TemplateCard from "../components/TemplateCard.jsx";
import TemplateConfirmationModal from "../components/TemplateConfirmationModal.jsx";
import API from "../config/api.js";
import { useDraftPrompt } from "../context/DraftPromptContext.jsx";

const MONTH_NAMES = {
  1: "January",
  2: "February",
  3: "March",
  4: "April",
  5: "May",
  6: "June",
  7: "July",
  8: "August",
  9: "September",
  10: "October",
  11: "November",
  12: "December",
};

function SocialMediaSlot({
  reel,
  template,
  reverse = false,
  isDesktopOnly = false,
  playingReelId,
  onTogglePreview,
  onVideoEnded,
  onUseTemplate,
}) {
  const columnClass = `col-6 col-md${
    isDesktopOnly ? " d-none d-md-block" : ""
  }`;

  return (
    <div className={columnClass}>
      {reverse ? (
        <>
          <div className="card-4-5 mb-3">
            <div className="card-content">
              {template ? (
                <TemplateCard template={template} onUse={onUseTemplate} />
              ) : null}
            </div>
          </div>

          <div className="card-9-16">
            <div className="card-content">
              {reel ? (
                <ReelCard
                  reel={reel}
                  isPlaying={playingReelId === reel.idreel}
                  onTogglePreview={onTogglePreview}
                  onVideoEnded={onVideoEnded}
                />
              ) : null}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="card-9-16 mb-3">
            <div className="card-content">
              {reel ? (
                <ReelCard
                  reel={reel}
                  isPlaying={playingReelId === reel.idreel}
                  onTogglePreview={onTogglePreview}
                  onVideoEnded={onVideoEnded}
                />
              ) : null}
            </div>
          </div>

          <div className="card-4-5">
            <div className="card-content">
              {template ? (
                <TemplateCard template={template} onUse={onUseTemplate} />
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function SocialMedia() {
  const location = useLocation();
  const navigate = useNavigate();

  const { patch, openDraftPrompt, clear } = useDraftPrompt();

  const [reels, setReels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingReelId, setPlayingReelId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  // Mês atual
  const currentMonth = new Date().getMonth() + 1;

  // Mês informado na URL
  const requestedMonth =
    Number.parseInt(
      params.get("month") || String(currentMonth),
      10
    ) || currentMonth;

  // Impede acesso a meses inválidos ou posteriores ao mês atual
  const month = Math.min(
    currentMonth,
    Math.max(1, Math.min(12, requestedMonth))
  );

  const year = new Date().getFullYear();

  const monthName = MONTH_NAMES[month];
  const shortMonth = monthName.slice(0, 3);
  const lastDay = new Date(year, month, 0).getDate();

  const prevMonth = month - 1;
  const nextMonth = month + 1;

  const scheduledLabels = useMemo(() => {
    return Array.from({ length: 31 }, (_, index) => {
      const day = index + 1;

      return day <= lastDay
        ? {
            day,
            month: shortMonth,
          }
        : null;
    });
  }, [lastDay, shortMonth]);

  const fetchSocialMedia = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${API.socialMedia}?month=${String(month).padStart(2, "0")}&list`,
        {
          credentials: "include",
        }
      );

      const data = await res.json();

      setReels(Array.isArray(data?.reels) ? data.reels : []);
      setTemplates(Array.isArray(data?.templates) ? data.templates : []);
    } catch (err) {
      console.error("Error", err);
      setReels([]);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSocialMedia();
    setPlayingReelId(null);
  }, [month]);

  useEffect(() => {
    const labels = document.querySelectorAll(".scheduled-date-time");

    labels.forEach((element, index) => {
      const info = scheduledLabels[index];

      if (!info) {
        element.innerHTML = "";
        return;
      }

      element.innerHTML = `
        <div
          class="bg-white text-dark rounded text-center lh-1 p-2"
          style="width: 45px; height: 41px; font-size: 12px"
        >
          <strong>${info.day}</strong><br>${info.month}
        </div>
      `;
    });
  }, [scheduledLabels, reels, templates]);

  const goToMonth = (value) => {
    // Não permite navegar para meses anteriores a janeiro
    // nem para meses posteriores ao mês atual.
    if (value < 1 || value > currentMonth) {
      return;
    }

    navigate(
      `/social-media?month=${String(value).padStart(2, "0")}`
    );
  };

  const handleTogglePreview = (idreel) => {
    setPlayingReelId((prev) => (prev === idreel ? null : idreel));
  };

  const handleVideoEnded = () => {
    setPlayingReelId(null);
  };

  const applyTemplateToDraft = (template) => {
    if (!template) return;

    clear();

    let ids = [];

    if (Array.isArray(template.idimages)) {
      ids = template.idimages;
    } else if (
      typeof template.idimages === "string" &&
      template.idimages.trim()
    ) {
      ids = template.idimages
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    }

    ids.forEach((id) => {
      window._draft_prompt_item_add?.(
        "image",
        id,
        `${API.baseUrl}/upload/image/${id}/image/original.jpg`
      );
    });

    patch({
      content: template.caption || "",
    });

    openDraftPrompt();
  };

  const handleUseTemplate = (template) => {
    if (!template) return;

    if (template.type === "images") {
      applyTemplateToDraft(template);
      return;
    }

    setSelectedTemplate(template);
  };

  const handleCloseConfirmation = () => {
    setSelectedTemplate(null);
  };

  const handleContinueConfirmation = () => {
    applyTemplateToDraft(selectedTemplate);
    setSelectedTemplate(null);
  };

  const slots = useMemo(() => {
    return [
      {
        reel: reels[0] ?? null,
        template: templates[0] ?? null,
        reverse: false,
        desktopOnly: false,
      },
      {
        reel: reels[1] ?? null,
        template: templates[1] ?? null,
        reverse: true,
        desktopOnly: false,
      },
      {
        reel: reels[2] ?? null,
        template: templates[2] ?? null,
        reverse: false,
        desktopOnly: false,
      },
      {
        reel: reels[3] ?? null,
        template: templates[3] ?? null,
        reverse: true,
        desktopOnly: false,
      },
      {
        reel: reels[4] ?? null,
        template: templates[4] ?? null,
        reverse: false,
        desktopOnly: true,
      },

      {
        reel: reels[5] ?? null,
        template: templates[5] ?? null,
        reverse: false,
        desktopOnly: false,
      },
      {
        reel: reels[6] ?? null,
        template: templates[6] ?? null,
        reverse: true,
        desktopOnly: false,
      },
      {
        reel: reels[7] ?? null,
        template: templates[7] ?? null,
        reverse: false,
        desktopOnly: false,
      },
      {
        reel: reels[8] ?? null,
        template: templates[8] ?? null,
        reverse: true,
        desktopOnly: false,
      },
      {
        reel: reels[9] ?? null,
        template: templates[9] ?? null,
        reverse: false,
        desktopOnly: true,
      },

      {
        reel: reels[10] ?? null,
        template: templates[10] ?? null,
        reverse: false,
        desktopOnly: false,
      },
      {
        reel: reels[11] ?? null,
        template: templates[11] ?? null,
        reverse: true,
        desktopOnly: false,
      },
      {
        reel: reels[12] ?? null,
        template: templates[12] ?? null,
        reverse: false,
        desktopOnly: false,
      },
      {
        reel: reels[13] ?? null,
        template: templates[13] ?? null,
        reverse: true,
        desktopOnly: false,
      },
      {
        reel: reels[14] ?? null,
        template: templates[14] ?? null,
        reverse: false,
        desktopOnly: true,
      },
    ];
  }, [reels, templates]);

  return (
    <AppLayout currentPage="social-media">
      <NavSocialMedia currentPage="social-media" />

      <div className="justify-content-center justify-content-md-end d-flex mb-3">
        <div className="border rounded-3 d-flex justify-content-between align-items-center px-1">
          {/* MÊS ANTERIOR */}
          {prevMonth >= 1 ? (
            <button
              type="button"
              onClick={() => goToMonth(prevMonth)}
              className="btn bg-white border-0 p-0"
            >
              <i className="fi fi-rr-angle-left"></i>
            </button>
          ) : (
            <button
              type="button"
              className="btn bg-white border-0 p-0 disabled"
              disabled
            >
              <i className="fi fi-rr-angle-left"></i>
            </button>
          )}

          {/* MÊS ATUAL */}
          <span className="btn bg-white border-0 disabled p-0">
            {monthName}
          </span>

          {/* PRÓXIMO MÊS */}
          {month < currentMonth ? (
            <button
              type="button"
              onClick={() => goToMonth(nextMonth)}
              className="btn bg-white border-0 p-0"
            >
              <i className="fi fi-rr-angle-right"></i>
            </button>
          ) : (
            <button
              type="button"
              className="btn bg-white border-0 p-0 disabled"
              disabled
            >
              <i className="fi fi-rr-angle-right"></i>
            </button>
          )}
        </div>
      </div>

      <div className="suggested-for-you">
        {[0, 1, 2].map((rowIndex) => {
          const rowSlots = slots.slice(
            rowIndex * 5,
            rowIndex * 5 + 5
          );

          return (
            <div key={rowIndex} className="row g-3 mb-3">
              {rowSlots.map((slot, index) => (
                <SocialMediaSlot
                  key={`${rowIndex}-${index}`}
                  reel={slot.reel}
                  template={slot.template}
                  reverse={slot.reverse}
                  isDesktopOnly={slot.desktopOnly}
                  playingReelId={playingReelId}
                  onTogglePreview={handleTogglePreview}
                  onVideoEnded={handleVideoEnded}
                  onUseTemplate={handleUseTemplate}
                />
              ))}
            </div>
          );
        })}
      </div>

      {loading && (
        <div className="text-center py-4">
          <div
            className="spinner-border"
            role="status"
            aria-hidden="true"
          ></div>
        </div>
      )}

      <TemplateConfirmationModal
        isOpen={Boolean(selectedTemplate)}
        editUrl={selectedTemplate?.url || ""}
        onClose={handleCloseConfirmation}
        onContinue={handleContinueConfirmation}
      />
    </AppLayout>
  );
}